import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, Observable, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { PERMISSION_REQUEST_ERRORS } from '../../enums/permission_request_error.enum';
import { AccessTypeSelection } from '../../../common/enums/access-type-selection.enum';
import { ClientTypes } from '../../../common/enums/client-types.enum';
import { LoggerService } from '../logger/logger.service';

export interface TokenValidationResponse {
    error: PERMISSION_REQUEST_ERRORS;
    accessLevel?: AccessTypeSelection;
    isExistingClient?: boolean;
    clientType: ClientTypes;
    isMultiAccount?: boolean;
}

@Injectable({
    providedIn: 'root',
})
export class TokenValidationService {
    private readonly httpClient = inject(HttpClient);
    private readonly loggerService = inject(LoggerService);


    /**
     * Makes the API call to validate a token.
     * 
     * @param token - The unique token to validate
     * @param endpoint - The API endpoint URL for validation
     * @returns Observable<TokenValidationResponse> - Contains error status, access level, and client type
     * 
     * @private
     * @remarks
     * Returns a default error response if the API call fails.
     */
    private requestTokenValidation(
        token: string,
        endpoint: string,
    ): Observable<TokenValidationResponse> {
        return this.httpClient.post<TokenValidationResponse>(endpoint, { token }).pipe(
            catchError(() => {
                this.loggerService.error(
                    `${TokenValidationService.name}: error while validating token: ${token}`,
                );
                return of({
                    error: PERMISSION_REQUEST_ERRORS.RECORD_NOT_FOUND,
                    clientType: ClientTypes.UNKNOWN,
                });
            }),
        );
    }

    /**
     * Validates a permission request token for authenticated routes.
     * 
     * @param token - The unique token from the permission request URL
     * @returns Observable<TokenValidationResponse> - Contains validation result, access level, and client type
     * 
     * @remarks
     * This API validates that:
     * - The token exists and is valid
     * - The token hasn't expired
     * - The permission request is in a valid state
     * Returns error codes and client information needed to route the user appropriately.
     */
    tokenValidation(token: string): Observable<TokenValidationResponse> {
        const endpoint = `${environment.ZCI_API_BASE_URL}${environment.ZCI_API_ENDPOINTS.TOKEN_VALIDATION}`;
        return this.requestTokenValidation(token, endpoint);
    }

    /**
     * Validates a permission request token for public (unauthenticated) routes.
     * 
     * @param token - The unique token from the permission request URL
     * @returns Observable<TokenValidationResponse> - Contains validation result, access level, and client type
     * 
     * @remarks
     * This is similar to tokenValidation but uses a public endpoint that doesn't
     * require authentication. Used for non-Vanguard client flows and initial token validation.
     */
    tokenValidationPublic(token: string): Observable<TokenValidationResponse> {
        const endpoint = `${environment.ZCI_API_BASE_URL}${environment.ZCI_API_ENDPOINTS.TOKEN_VALIDATION_PUBLIC}`;
        return this.requestTokenValidation(token, endpoint);
    }
}
